import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';

import { Observable, of as observableOf } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { IAutoCompleteLookup, IAutoCompletePredictions } from './autocomplete.interface';
import { IAddressCollection } from '../input.address.lookup/input.address.lookup.interface';

export class PostcodeLookUpServiceConfig {
    autoCompleteDomain = '';
    geoCodeDomain = '';
    apiKey = '';
}

@Injectable()
export class InputPostcodeLookUpService {
    autoCompleteDomain = '';
    geoCodeDomain = '';
    apiKey = '';

    constructor(private http: HttpClient, @Optional() config: PostcodeLookUpServiceConfig) {
        if (config) {
            this.autoCompleteDomain = config.autoCompleteDomain;
            this.geoCodeDomain = config.geoCodeDomain;
            this.apiKey = config.apiKey;
        }
    }

    getAutoComplete(input: Observable<string>, types: string, countries: string): Observable<Array<IAutoCompleteLookup>> {
        return input.pipe(
            debounceTime(250),
            distinctUntilChanged(),
            filter(addData => addData !== ''),
            switchMap(add => {
                return this.http.get(
                    // tslint:disable-next-line:max-line-length
                    `${this.autoCompleteDomain}/maps/api/place/autocomplete/json?input=${add}&types=${types}&components=${countries}&key=${this.apiKey}`);
            }),
            map((results: IAutoCompletePredictions) => this.getAllPredictions(results)));
    }

    getFirstAutoComplete(input: string, types: string, countries: string): Observable<undefined | {latitude: number, longitude: number}> {
        return this.http.get(
            // tslint:disable-next-line:max-line-length
            `${this.autoCompleteDomain}/maps/api/place/autocomplete/json?input=${input}&types=${types}&components=${countries}&key=${this.apiKey}`)
            .pipe(
                map((results: IAutoCompletePredictions) => this.getFirstResultFromPredictions(results)),
                switchMap(
                    (data: IAutoCompleteLookup) => data ? this.getLatLongFromPlaceId(data.place_id) : observableOf(undefined)
                )
            );
    }

    getLatLongFromPlaceId(placeId: string): Observable<{latitude: number, longitude: number}> {
        return this.http.get
        (`${this.geoCodeDomain}/maps/api/geocode/json?place_id=${placeId}&key=${this.apiKey}`)
            .pipe(
                map((results: IAddressCollection) => this.returnLatLong(results))
            );
    }

    protected getAllPredictions(results: IAutoCompletePredictions): Array<IAutoCompleteLookup> {
        const suggestions = [];

        results.predictions.forEach(item => {
            if (item.types.indexOf('postal_code') !== -1) {
                suggestions.push(item);
            }
        });

        return suggestions;
    }

    protected getFirstResultFromPredictions(results: IAutoCompletePredictions): IAutoCompleteLookup {
        let suggestion;

        results.predictions.forEach(item => {
            if (item.types.indexOf('postal_code') !== -1 && !suggestion) {
                suggestion = item;
            }
        });

        return suggestion;
    }

    protected returnLatLong(results: IAddressCollection): {latitude: number, longitude: number} {
        let result;

        results.results.forEach(item => {
            if (item.types.indexOf('postal_code') !== -1 && !result) {
                result = {latitude: item.geometry.location.lat, longitude: item.geometry.location.lng};
            }
        });

        return result;
    }

}
